"use client";

import { c as _c } from "react/compiler-runtime";
import { NavigationLinkStatus } from "@/components/navigation/NavigationFeedback/NavigationLinkStatus";
import { markTabSelectionPending } from "@/utils/tab-selection-feedback.client";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type { ComponentProps } from "react";
import { useCallback, useRef } from "react";
import { preloadRouteClientForHref } from "./route-client-preload";
type IntentPrefetchLinkProps = Omit<ComponentProps<typeof Link>, "href" | "prefetch"> & {
  href: string;
};

/**
 * Keeps viewport-wide Next.js prefetch disabled, then asks Next's route cache
 * to warm the route when pointer, keyboard, or touch intent is signaled.
 */
/** Use when an in-app link should warm its route on hover, focus or touch instead of prefetching on view. */
export function IntentPrefetchLink(t0) {
  const $ = _c(36);
  let children;
  let href;
  let onClick;
  let onFocus;
  let onPointerEnter;
  let onTouchStart;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      href,
      onClick,
      onFocus,
      onPointerEnter,
      onTouchStart,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = href;
    $[3] = onClick;
    $[4] = onFocus;
    $[5] = onPointerEnter;
    $[6] = onTouchStart;
    $[7] = props;
  } else {
    children = $[1];
    href = $[2];
    onClick = $[3];
    onFocus = $[4];
    onPointerEnter = $[5];
    onTouchStart = $[6];
    props = $[7];
  }
  const router = useRouter();
  const prefetchedHrefRef = useRef(null);
  let t1;
  if ($[8] !== href) {
    t1 = () => {
      if (prefetchedHrefRef.current === href) {
        prefetchedHrefRef.current = null;
      }
    };
    $[8] = href;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const resetPrefetchGuard = t1;
  let t2;
  if ($[10] !== href || $[11] !== resetPrefetchGuard || $[12] !== router) {
    t2 = () => {
      if (prefetchedHrefRef.current === href) {
        return;
      }
      prefetchedHrefRef.current = href;
      preloadRouteClientForHref(href);
      router.prefetch(href, {
        kind: "full",
        onInvalidate: resetPrefetchGuard
      } as Parameters<typeof router.prefetch>[1]);
    };
    $[10] = href;
    $[11] = resetPrefetchGuard;
    $[12] = router;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const prefetchOnIntent = t2;
  let t3;
  if ($[14] !== onClick) {
    t3 = event => {
      onClick?.(event);
      if (!event.defaultPrevented && event.button === 0 && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.currentTarget.getAttribute("role") === "tab") {
        markTabSelectionPending(event.currentTarget);
      }
    };
    $[14] = onClick;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== onFocus || $[17] !== prefetchOnIntent) {
    t4 = event_0 => {
      onFocus?.(event_0);
      if (!event_0.defaultPrevented) {
        prefetchOnIntent();
      }
    };
    $[16] = onFocus;
    $[17] = prefetchOnIntent;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== onPointerEnter || $[20] !== prefetchOnIntent) {
    t5 = event_1 => {
      onPointerEnter?.(event_1);
      if (!event_1.defaultPrevented) {
        prefetchOnIntent();
      }
    };
    $[19] = onPointerEnter;
    $[20] = prefetchOnIntent;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== onTouchStart || $[23] !== prefetchOnIntent) {
    t6 = event_2 => {
      onTouchStart?.(event_2);
      if (!event_2.defaultPrevented) {
        prefetchOnIntent();
      }
    };
    $[22] = onTouchStart;
    $[23] = prefetchOnIntent;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== href) {
    t7 = <NavigationLinkStatus href={href} />;
    $[25] = href;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== children || $[28] !== href || $[29] !== props || $[30] !== t3 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7) {
    t8 = <Link {...props} href={href} prefetch={false} onClick={t3} onFocus={t4} onPointerEnter={t5} onTouchStart={t6}>{children}{t7}</Link>;
    $[27] = children;
    $[28] = href;
    $[29] = props;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
}